import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { map, Observable, switchMap, tap } from 'rxjs';

import {
	ALL,
	AnalyticsEvent,
	OrderDirection,
	Org,
	ProjectComment,
	ProjectStage,
	SearchAutocompleteResult,
	User,
} from '@efficiently/models';

import { OrgRepository } from '@efficiently/client-api-org';
import { OrganizationFilter, ProjectsRepository } from '@efficiently/client-api-projects';
import { UsersRepository } from '@efficiently/client-api-users';
import { AnalyticsService } from '@efficiently/client-core-analytics';
import type { GetProjectsResult, UpdateProjectInput, UpdateProjectMutation } from '@efficiently/client-domain-projects';
import { EnumDelimiterPipe } from '@efficiently/client-ui-common';

import {
	AddProjectFiltersAction,
	AddProjectSortingAction,
	ArchiveProjectAction,
	CloneProject,
	GetOrgsAction,
	GetProjectListAction,
	GetProjectListAutocompleteAction,
	GetProjectOwnersAction,
	PatchFilters,
	ResetProjectListMentionsAction,
	SetAsDemoProject,
	UpdateProjectListAction,
} from './project-list.actions';
import { defaults, ProjectListStateModel, ProjectOwner, ProjectUi } from './project-list-state.model';

@State<ProjectListStateModel>({
	name: 'projectList',
	defaults,
})
@Injectable()
export class ProjectListState {
	private readonly usersRepository = inject(UsersRepository);
	private readonly projectsRepository = inject(ProjectsRepository);
	private readonly orgGqlService = inject(OrgRepository);
	private readonly enumDelimiter = inject(EnumDelimiterPipe);
	private readonly analyticsService = inject(AnalyticsService);

	@Action(UpdateProjectListAction)
	public updateProjectList(
		{ patchState }: StateContext<ProjectListStateModel>,
		{ payload }: UpdateProjectListAction,
	): void {
		patchState({ projects: payload });
	}

	@Action(ArchiveProjectAction)
	public archivateProject(
		{ patchState, getState, dispatch }: StateContext<ProjectListStateModel>,
		{ payload }: ArchiveProjectAction,
	): Observable<UpdateProjectMutation['updateProject']> {
		patchState({ pending: true });
		const { filters, pagination } = getState();

		const stage = filters?.status === ProjectStage.Archived ? ProjectStage.Active : ProjectStage.Archived;
		const updatePayload: UpdateProjectInput = { _id: payload._id, stage };

		let currentPage = pagination.page;

		if ((pagination.total ?? 0) % pagination.size === 1) {
			currentPage = Math.max(1, currentPage - 1);
		}
		this.analyticsService.track(AnalyticsEvent.ArchiveProject);
		return this.projectsRepository.updateProject(updatePayload).pipe(
			tap(() => {
				dispatch(new GetProjectListAction(currentPage));
			}),
		);
	}

	@Action(SetAsDemoProject)
	public setAsDemoProject(
		{ patchState, getState, dispatch }: StateContext<ProjectListStateModel>,
		{ payload }: SetAsDemoProject,
	): Observable<UpdateProjectMutation['updateProject']> {
		patchState({ pending: true });

		const { pagination } = getState();
		let currentPage = pagination.page;

		if ((pagination.total ?? 0) % pagination.size === 1) {
			currentPage = Math.max(1, currentPage - 1);
		}

		const updatePayload: UpdateProjectInput = { _id: payload._id, isSample: payload.isSample };

		return this.projectsRepository.updateProject(updatePayload).pipe(
			tap(() => {
				dispatch(new GetProjectListAction(currentPage));
			}),
		);
	}

	@Action(AddProjectSortingAction)
	public addProjectSorting(
		{ patchState, dispatch }: StateContext<ProjectListStateModel>,
		{ payload }: AddProjectSortingAction,
	): void {
		patchState({ sorting: payload });
		dispatch(new GetProjectListAction());
	}

	@Action(AddProjectFiltersAction)
	public addProjectFilters(
		{ dispatch }: StateContext<ProjectListStateModel>,
		{ payload }: AddProjectFiltersAction,
	): void {
		dispatch([new PatchFilters(payload), new GetProjectListAction()]);
	}

	@Action(PatchFilters)
	public patchFilters(
		{ patchState, getState }: StateContext<ProjectListStateModel>,
		{ payload }: PatchFilters,
	): void {
		const { filters } = getState();

		patchState({
			filters: {
				...filters,
				status: payload.status ?? filters.status,
				searchQuery: payload.searchQuery ?? filters.searchQuery,
				searchField: payload.searchField === undefined ? filters.searchField : payload.searchField,
				// https://earthelements.atlassian.net/browse/EFF-6474
				// need to be refactored after correct mapping
				owner: payload.owner ?? filters.owner,
				organization: payload.organization ?? filters.organization,
				comments: payload.comments ?? filters.comments,
			},
		});
	}

	@Action(GetProjectOwnersAction)
	public getProjectOwners({ patchState }: StateContext<ProjectListStateModel>): Observable<User[]> {
		return this.projectsRepository.getProjectOwners().pipe(
			tap((response: User[]) => {
				patchState({
					projectOwners: response.map(user => new ProjectOwner(user._id, user.name)),
				});
			}),
		);
	}

	@Action(GetOrgsAction)
	public getOrgs({ patchState }: StateContext<ProjectListStateModel>): Observable<Org[]> {
		return this.orgGqlService.getPolicyOrgs().pipe(
			tap((orgs: Org[]) => {
				const organizations: OrganizationFilter[] = orgs.map(org => ({
					id: org._id,
					name: org.account.name,
					isDefault: false,
				}));
				patchState({ organizations });
			}),
		);
	}

	@Action(GetProjectListAction)
	public getProjectList(
		{ getState, patchState, dispatch }: StateContext<ProjectListStateModel>,
		{ payload }: GetProjectListAction,
	): Observable<{ resp: GetProjectsResult; users: User[] }> {
		let { pagination } = getState();
		const { filters } = getState();
		const { sorting } = getState();
		pagination = { ...pagination, page: payload };

		patchState({ pending: true });

		return this.projectsRepository
			.getProjects({
				search: filters.searchQuery,
				searchField: filters.searchField,
				stage: filters.status,
				// https://earthelements.atlassian.net/browse/EFF-6474
				// need to be refactored after correct mapping
				projectOwner: filters.owner === ALL ? '' : filters.owner,
				orgId: filters.organization === ALL ? '' : filters.organization,
				pagination,
				orderBy: sorting?.orderBy,
				orderDirection: sorting?.orderDirection ?? OrderDirection.DESC,
				commentType: {
					activeRFIs: filters.comments === ProjectComment.ActiveRFIs,
					mentions: filters.comments === ProjectComment.Mentions,
					unreadComments: filters.comments === ProjectComment.UnreadComments,
				},
			})
			.pipe(
				switchMap(resp => {
					const userIds = [
						...new Set(resp.projects.map(p => p.createdById).filter((id): id is string => id != null)),
					];
					return this.usersRepository.getUsers(userIds).pipe(map(users => ({ resp, users })));
				}),
				tap(({ resp, users }) => {
					const projects: ProjectUi[] = resp.projects.map(project => ({
						...project,
						typeLabel: this.enumDelimiter.transform(project.type),
						viewOnly: project.isEditAvailable === false,
						createdByName: users.find(u => u._id === project.createdById)?.name,
						organization: project.orgEntity?.account?.name ?? '',
					}));

					patchState({ pagination: resp.pagination ?? pagination });
					dispatch(new UpdateProjectListAction(projects));
					patchState({ pending: false });
				}),
			);
	}

	@Action(GetProjectListAutocompleteAction)
	public getProjectListMentions({
		getState,
		patchState,
	}: StateContext<ProjectListStateModel>): Observable<SearchAutocompleteResult[]> {
		const { filters, sorting } = getState();
		patchState({ isFetchingAutocomplete: true });

		return this.projectsRepository
			.getProjectAutocompleteValues({
				search: filters.searchQuery,
				stage: filters.status,
				projectOwner: filters.owner === ALL ? '' : filters.owner,
				orgId: filters.organization === ALL ? '' : filters.organization,
				orderBy: sorting?.orderBy,
				orderDirection: sorting?.orderDirection || OrderDirection.DESC,
			})
			.pipe(
				tap(resp => {
					patchState({ autocompleteSearchOptions: resp, isFetchingAutocomplete: false });
				}),
			);
	}

	@Action(ResetProjectListMentionsAction)
	public resetProjectListMentions({ patchState, getState }: StateContext<ProjectListStateModel>): void {
		const { filters } = getState();
		patchState({ autocompleteSearchOptions: [], filters });
	}

	@Action(CloneProject)
	public cloneProject(
		{ dispatch, patchState, getState }: StateContext<ProjectListStateModel>,
		{ payload }: CloneProject,
	): Observable<string> {
		const { pagination } = getState();
		return this.projectsRepository.cloneProject(payload).pipe(
			tap(clonedProjectId => {
				patchState({ clonedProjectId });
				dispatch(new GetProjectListAction(pagination.page));
				patchState({ pending: false });
			}),
		);
	}
}
